import React, { createContext, useContext, useReducer, useEffect } from 'react'
import { authService } from '@services/enhancedApiService'
import toast from 'react-hot-toast'
import logger from '@utils/logger'
import { authenticationRedirect } from '@utils/x3ConnectAuthorization'
import { authAttempts } from '@utils/authAttemptState'

// Auth Context
const AuthContext = createContext()

// Auth Actions
const AUTH_ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_USER: 'SET_USER',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGOUT_PENDING: 'LOGOUT_PENDING',
  LOGOUT: 'LOGOUT',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR'
}

// Initial state
const initialState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null
}

// Auth reducer
function authReducer(state, action) {
  switch (action.type) {
    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload
      }

    case AUTH_ACTIONS.SET_USER:
      return {
        ...state,
        user: action.payload,
        isAuthenticated: !!action.payload,
        isLoading: false,
        error: null
      }

    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        isLoading: false,
        error: null
      }

    case AUTH_ACTIONS.LOGOUT:
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null
      }

    case AUTH_ACTIONS.LOGOUT_PENDING:
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: true,
        error: null
      }

    case AUTH_ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        isLoading: false
      }

    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null
      }

    default:
      return state
  }
}

/**
 * Auth Provider Component
 */
export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(authReducer, initialState)

  // Check authentication status on mount
  useEffect(() => {
    // No need to check URL hash - authentication is handled via HTTP-only cookies
    // Cookies are automatically sent with cross-subdomain support (.x3algo.com)
    checkAuthStatus()
    const onAuthenticationFailure = () => {
      dispatch({ type: AUTH_ACTIONS.LOGOUT })
    }
    window.addEventListener('auth:logout', onAuthenticationFailure)
    return () => {
      window.removeEventListener('auth:logout', onAuthenticationFailure)
      authAttempts.invalidate('provider-unmount')
    }
  }, [])

  /**
   * Check if user is authenticated
   */
  const checkAuthStatus = async () => {
    const attempt = authAttempts.capture()
    const authStatus = authAttempts.getStatus()
    if (authStatus.blocked && !authStatus.logoutPending) {
      localStorage.removeItem('user')
      localStorage.removeItem('tokenMetadata')
      dispatch({ type: AUTH_ACTIONS.SET_USER, payload: null })
      const loginUrl = import.meta.env.VITE_LOGIN_URL || 'http://localhost:3000/login'
      window.location.href = authenticationRedirect(loginUrl, window.location)
      return
    }
    try {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true })

      const snapshot = authService.getCurrentUserSnapshot
        ? await authService.getCurrentUserSnapshot(attempt, {
          skipAuthRefresh: authStatus.logoutPending
        })
        : { user: await authService.getCurrentUser(attempt) }
      if (!authAttempts.isCurrent(attempt)) return
      const user = snapshot?.user

      if (user) {
        dispatch({ type: AUTH_ACTIONS.SET_USER, payload: user })
      } else {
        dispatch({ type: AUTH_ACTIONS.SET_USER, payload: null })
        // Redirect to login if no user data
        const loginUrl =
          import.meta.env.VITE_LOGIN_URL || 'http://localhost:3000/login'
        window.location.href = authenticationRedirect(loginUrl, window.location)
      }
    } catch (error) {
      if (!authAttempts.isCurrent(attempt)) return
      if (error.response?.status === 401 || error.requiresReauthentication) {
        authAttempts.requireReauthentication()
        dispatch({ type: AUTH_ACTIONS.SET_USER, payload: null })
        const loginUrl = import.meta.env.VITE_LOGIN_URL || 'http://localhost:3000/login'
        window.location.href = authenticationRedirect(loginUrl, window.location)
      } else {
        if (error.code === 'AUTH_LOGOUT_PENDING') {
          dispatch({ type: AUTH_ACTIONS.SET_USER, payload: null })
          const loginUrl = import.meta.env.VITE_LOGIN_URL || 'http://localhost:3000/login'
          window.location.href = authenticationRedirect(loginUrl, window.location)
          return
        }
        dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: 'Unable to verify your session. Try again shortly.' })
      }
    }
  }

  useEffect(() => authAttempts.subscribe(change => {
    if (change.kind !== 'peer-change') return
    if (change.blocked) {
      localStorage.removeItem('user')
      localStorage.removeItem('tokenMetadata')
          dispatch({
            type: change.logoutPending
              ? AUTH_ACTIONS.LOGOUT_PENDING
              : AUTH_ACTIONS.LOGOUT
          })
      return
    }
    checkAuthStatus()
  }), [])

  /**
   * Logout user
   */
  const logout = async () => {
    const attempt = authAttempts.beginLogout()
    localStorage.removeItem('user')
    localStorage.removeItem('mockToken')
    localStorage.removeItem('tokenMetadata')
    localStorage.removeItem('notification_preferences')
    localStorage.removeItem('fcm_token')
    dispatch({ type: AUTH_ACTIONS.LOGOUT_PENDING })
    try {
      const result = await authService.logout(attempt)
      if (!authAttempts.finishLogout(attempt, result.serverRevoked)) {
        return { localSignedOut: true, serverRevoked: false, stale: true }
      }
      if (result.serverRevoked) toast.success('Logged out successfully')
      else toast.error('Signed out here, but server sign-out could not be confirmed.')
      const finished = authAttempts.capture()
      setTimeout(() => {
        if (authAttempts.isCurrent(finished)) {
          window.location.href =
            import.meta.env.VITE_LANDING_URL || 'https://x3algo.com/'
        }
      }, 1000)
      return { localSignedOut: true, serverRevoked: result.serverRevoked }
    } catch {
      if (!authAttempts.finishLogout(attempt, false)) {
        return { localSignedOut: true, serverRevoked: false, stale: true }
      }
      window.location.href =
        import.meta.env.VITE_LANDING_URL || 'https://x3algo.com/'
      return { localSignedOut: true, serverRevoked: false }
    }
  }

  /**
   * Update user profile
   */
  const updateProfile = async profileData => {
    const attempt = authAttempts.capture()
    try {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true })
      dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR })

      const response = await authService.updateProfile(profileData, attempt)
      if (!authAttempts.isCurrent(attempt)) return { success: false, stale: true }

      if (response.success) {
        dispatch({ type: AUTH_ACTIONS.SET_USER, payload: response.user })
        toast.success('Profile updated successfully')
        return { success: true }
      } else {
        throw new Error(response.message || 'Profile update failed')
      }
    } catch (error) {
      if (!authAttempts.isCurrent(attempt)) return { success: false, stale: true }
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Profile update failed'
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: errorMessage })
      toast.error(errorMessage)
      return { success: false, error: errorMessage }
    }
  }

  /**
   * Change password
   */
  const changePassword = async passwordData => {
    try {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true })
      dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR })

      const response = await authService.changePassword(passwordData)

      if (response.success) {
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false })
        toast.success('Password changed successfully')
        return { success: true }
      } else {
        throw new Error(response.message || 'Password change failed')
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Password change failed'
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: errorMessage })
      toast.error(errorMessage)
      return { success: false, error: errorMessage }
    }
  }

  /**
   * Clear error state
   */
  const clearError = () => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR })
  }

  /**
   * Refresh user data
   */
  const refreshUser = async () => {
    const attempt = authAttempts.capture()
    try {
      const snapshot = authService.getCurrentUserSnapshot
        ? await authService.getCurrentUserSnapshot(attempt)
        : { user: await authService.getCurrentUser(attempt) }
      if (!authAttempts.isCurrent(attempt)) return null
      const user = snapshot?.user
      dispatch({ type: AUTH_ACTIONS.SET_USER, payload: user })
      return user
    } catch (error) {
      if (authAttempts.isCurrent(attempt) && error.response?.status === 401) {
        dispatch({ type: AUTH_ACTIONS.LOGOUT })
      }
      return null
    }
  }

  /**
   * Upload avatar
   */
  const uploadAvatar = async file => {
    const attempt = authAttempts.capture()
    try {
      const response = await authService.uploadAvatar(file, attempt)
      if (!authAttempts.isCurrent(attempt)) return { success: false, stale: true }

      if (response.success) {
        // Update user with new avatar
        const updatedUser = { ...state.user, avatar: response.data.avatarUrl }
        dispatch({ type: AUTH_ACTIONS.SET_USER, payload: updatedUser })
        toast.success('Avatar uploaded successfully')
        return { success: true, avatarUrl: response.data.avatarUrl }
      } else {
        throw new Error(response.message || 'Avatar upload failed')
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || error.message || 'Avatar upload failed'
      toast.error(errorMessage)
      throw new Error(errorMessage)
    }
  }

  /**
   * Delete avatar
   */
  const deleteAvatar = async () => {
    const attempt = authAttempts.capture()
    try {
      const response = await authService.deleteAvatar(attempt)
      if (!authAttempts.isCurrent(attempt)) return { success: false, stale: true }

      if (response.success) {
        // Update user with null avatar
        const updatedUser = { ...state.user, avatar: null }
        dispatch({ type: AUTH_ACTIONS.SET_USER, payload: updatedUser })
        toast.success('Avatar deleted successfully')
        return { success: true }
      } else {
        throw new Error(response.message || 'Avatar deletion failed')
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Avatar deletion failed'
      toast.error(errorMessage)
      throw new Error(errorMessage)
    }
  }

  /**
   * Resend the verification email for the authenticated user.
   */
  const resendVerification = async () => {
    try {
      if (!state.user?.email) {
        throw new Error('No email address is available for this account')
      }

      const response = await authService.resendVerification(state.user.email)
      if (!response.success) {
        throw new Error(response.message || 'Failed to send verification email')
      }

      toast.success(response.message || 'Verification email queued')
      return {
        success: true,
        message: response.message
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Failed to send verification email'
      logger.error('Resend verification error:', error)
      toast.error(errorMessage)
      return {
        success: false,
        message: errorMessage
      }
    }
  }

  const value = {
    // State
    user: state.user,
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading,
    error: state.error,

    // Actions
    logout,
    updateProfile,
    changePassword,
    clearError,
    refreshUser,
    checkAuthStatus,
    uploadAvatar,
    deleteAvatar,
    resendVerification
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

/**
 * Custom hook to use auth context
 */
export function useAuth() {
  const context = useContext(AuthContext)

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }

  return context
}

export default AuthContext
